import React, { useState, useCallback } from 'react';
import { X, CheckCircle2, Info, User, Mail, Phone, Hash, Rocket, Loader2, Clock, MapPin, Users, ChevronRight } from 'lucide-react';
import { FormField } from './Shared';
import { EVENT_SCRIPT_URL } from '../data/websiteData';

const EventModal = ({ event, onClose, showToast }) => {
  const [tab, setTab] = useState('details');
  const [rsvp, setRsvp] = useState({ name: '', email: '' });
  const [reg, setReg] = useState({ name: '', email: '', phone: '', roll: '' });
  const [rsvpErrors, setRsvpErrors] = useState({});
  const [regErrors, setRegErrors] = useState({});
  const [rsvpStatus, setRsvpStatus] = useState('idle');
  const [regStatus, setRegStatus] = useState('idle');
  const pct = Math.round((event.registered / event.seats) * 100);

  const handleRsvpChange = useCallback((id, val) => { 
    setRsvp(p => ({ ...p, [id]: val })); 
    setRsvpErrors(p => ({ ...p, [id]: '' })); 
  }, []);

  const handleRegChange = useCallback((id, val) => { 
    setReg(p => ({ ...p, [id]: val })); 
    setRegErrors(p => ({ ...p, [id]: '' })); 
  }, []);

  const submitRsvp = async e => {
    e.preventDefault();
    const errs = {};
    if (!rsvp.name.trim()) errs.name = 'Required';
    if (!rsvp.email.trim() || !/\S+@\S+\.\S+/.test(rsvp.email)) errs.email = 'Valid email required';
    if (Object.keys(errs).length) { setRsvpErrors(errs); return; }
    setRsvpStatus('loading');
    try {
      await fetch(EVENT_SCRIPT_URL, { 
        method: 'POST', 
        mode: 'no-cors', 
        headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify({ type: 'RSVP', eventId: event.id, eventTitle: event.title, name: rsvp.name, email: rsvp.email, timestamp: new Date().toISOString() }) 
      });
      setRsvpStatus('success'); 
      showToast(`RSVP confirmed for "${event.title}"!`, 'success'); 
      setRsvp({ name: '', email: '' });
    } catch { 
      showToast('RSVP failed. Please try again.', 'error'); 
      setRsvpStatus('idle'); 
    }
  };

  const submitReg = async e => {
    e.preventDefault();
    const errs = {};
    if (!reg.name.trim()) errs.name = 'Required';
    if (!reg.email.trim() || !/\S+@\S+\.\S+/.test(reg.email)) errs.email = 'Valid email required';
    if (!reg.phone.trim()) errs.phone = 'Required';
    if (!reg.roll.trim()) errs.roll = 'Required';
    if (Object.keys(errs).length) { setRegErrors(errs); return; }
    setRegStatus('loading');
    try {
      await fetch(EVENT_SCRIPT_URL, { 
        method: 'POST', 
        mode: 'no-cors', 
        headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify({ type: 'REGISTER', eventId: event.id, eventTitle: event.title, name: reg.name, email: reg.email, phone: reg.phone, roll: reg.roll, timestamp: new Date().toISOString() }) 
      });
      setRegStatus('success'); 
      showToast(`Registered for "${event.title}"!`, 'success'); 
      setReg({ name: '', email: '', phone: '', roll: '' });
    } catch { 
      showToast('Registration failed. Please try again.', 'error'); 
      setRegStatus('idle'); 
    }
  };

  const SuccessView = ({ title, sub, onReset }) => (
    <div style={{ textAlign: 'center', padding: '28px 0' }} className="af">
      <div style={{ width: 56, height: 56, borderRadius: 8, background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', animation: 'checkPop .5s ease' }}>
        <CheckCircle2 size={24} style={{ color: 'var(--green)' }} />
      </div>
      <h4 style={{ fontSize: 17, fontWeight: 700, color: 'var(--slate-900)', marginBottom: 6 }}>{title}</h4>
      <p style={{ fontSize: 13.5, color: 'var(--slate-500)', marginBottom: 16 }}>{sub}</p>
      <button className="btn btn-ghost btn-sm" onClick={onReset}>Submit Another</button>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(25,28,30,.4)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20
    }}>
      <div className="modal-box card" style={{
        width: '100%', maxWidth: 540, maxHeight: '90vh', overflowY: 'auto', background: 'var(--surface)', position: 'relative', animation: 'scaleIn .3s ease', borderRadius: 'var(--radius-lg)'
      }}>
        <div style={{ padding: '22px 22px 0', position: 'relative' }}>
          <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, width: 32, height: 32, borderRadius: 'var(--radius-sm)', background: 'var(--slate-100)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--slate-500)', zIndex: 10 }}><X size={16} /></button>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
            <div style={{ textAlign: 'center', background: 'rgba(0,101,113,.07)', borderRadius: 'var(--radius-md)', padding: '10px 12px', minWidth: 60, flexShrink: 0 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 700, color: 'var(--cyan-dim)', letterSpacing: '.1em' }}>{event.month}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--cyan-dim)', lineHeight: 1.1 }}>{event.day}</div>
            </div>
            <div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 5, flexWrap: 'wrap' }}>
                <span className={`badge ${event.bc}`}>{event.type}</span>
                <span className="badge bw" style={{ background: '#f1f5f9', color: '#64748b' }}>{event.status}</span>
              </div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(14px,2.5vw,18px)', fontWeight: 600, color: 'var(--on-surface)', lineHeight: 1.3 }}>{event.title}</h3>
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--slate-400)', marginBottom: 5 }}>
              <span>Capacity</span>
              <span style={{ color: pct > 80 ? 'var(--red)' : 'var(--cyan-dim)', fontWeight: 600 }}>{event.registered}/{event.seats} ({pct}%)</span>
            </div>
            <div className="prog-track"><div className="prog-fill" style={{ width: `${pct}%`, background: pct > 80 ? 'linear-gradient(90deg,var(--amber),var(--red))' : undefined }} /></div>
          </div>
          <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)' }}>
            {[['details', 'Details'], ['rsvp', 'RSVP (Free)'], ['register', 'Register']].map(([t, l]) => (
              <button key={t} onClick={() => setTab(t)} style={{ padding: '8px 12px', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none', background: 'none', color: tab === t ? 'var(--cyan-dim)' : 'var(--slate-400)', borderBottom: `2px solid ${tab === t ? 'var(--cyan-dim)' : 'transparent'}`, marginBottom: -1, transition: 'all .2s' }}>{l}</button>
            ))}
          </div>
        </div>
        <div style={{ padding: 22 }}>
          {tab === 'details' && (
            <div className="af">
              <div className="ev-detail-grid" style={{ marginBottom: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[[<Clock size={12} />, 'Time', event.time], [<MapPin size={12} />, 'Location', event.loc], [<User size={12} />, 'Speaker', event.speaker], [<Users size={12} />, 'Seats', `${event.seats} total`]].map(([ico, l, v]) => (
                  <div key={l} style={{ background: 'var(--surface-container-low)', borderRadius: 8, padding: '9px 11px', display: 'flex', gap: 7 }}>
                    <span style={{ color: 'var(--cyan-dim)', marginTop: 1, flexShrink: 0 }}>{ico}</span>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--slate-400)', textTransform: 'uppercase', letterSpacing: '.07em' }}>{l}</div>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--slate-700)' }}>{v}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: 13.5, color: 'var(--slate-600)', lineHeight: 1.75, marginBottom: 14 }}>{event.desc}</p>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate-700)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>Agenda</div>
                {event.agenda.map((a, i) => (
                  <div key={i} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 12.5, color: 'var(--slate-600)', padding: '7px 9px', background: 'var(--surface-container-low)', borderRadius: 8, marginBottom: 4 }}>
                    <span style={{ width: 17, height: 17, borderRadius: '50%', background: 'rgba(0,101,113,.07)', color: 'var(--cyan-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                    {a}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 16 }}>
                {event.tags.map(t => <span key={t} style={{ fontSize: 11, fontWeight: 600, background: 'rgba(0,101,113,.07)', color: 'var(--cyan-dim)', padding: '3px 9px', borderRadius: 100 }}>{t}</span>)}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setTab('rsvp')}>RSVP (Free)</button>
                <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setTab('register')}>Register <ChevronRight size={13} /></button>
              </div>
            </div>
          )}
          {tab === 'rsvp' && (
            <div className="af">
              <div style={{ marginBottom: 14, padding: 11, background: 'rgba(0,101,113,.07)', borderRadius: 8, fontSize: 13, color: 'var(--cyan-dim)', display: 'flex', gap: 8 }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                RSVP is free, it lets us plan capacity. Register if you want a confirmed seat and certificate.
              </div>
              {rsvpStatus === 'success' ? <SuccessView title="RSVP Confirmed!" sub="We'll remind you 24 hours before the event." onReset={() => setRsvpStatus('idle')} /> : (
                <form onSubmit={submitRsvp} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <FormField id="name" label="Full Name" placeholder="Your name" value={rsvp.name} onChange={handleRsvpChange} error={rsvpErrors.name} icon={<User size={13} />} />
                  <FormField id="email" label="Email Address" placeholder="your@email.com" type="email" value={rsvp.email} onChange={handleRsvpChange} error={rsvpErrors.email} icon={<Mail size={13} />} />
                  <button className="btn btn-outline btn-lg" type="submit" style={{ width: '100%', justifyContent: 'center' }} disabled={rsvpStatus === 'loading'}>
                    {rsvpStatus === 'loading' ? <><Loader2 size={14} className="spin" /> Submitting…</> : 'Confirm RSVP'}
                  </button>
                </form>
              )}
            </div>
          )}
          {tab === 'register' && (
            <div className="af">
              <div style={{ marginBottom: 14, padding: 11, background: 'var(--green-light)', borderRadius: 8, fontSize: 13, color: 'var(--green)', display: 'flex', gap: 8 }}>
                <CheckCircle2 size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                Registration gives you a confirmed seat and an attendance certificate.
              </div>
              {regStatus === 'success' ? <SuccessView title="Registration Successful!" sub="Bring your student ID on the day." onReset={() => setRegStatus('idle')} /> : (
                <form onSubmit={submitReg} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <FormField id="name" label="Full Name" placeholder="Your full name" value={reg.name} onChange={handleRegChange} error={regErrors.name} icon={<User size={13} />} />
                  <FormField id="email" label="Email Address" placeholder="your@email.com" type="email" value={reg.email} onChange={handleRegChange} error={regErrors.email} icon={<Mail size={13} />} />
                  <div className="g2">
                    <FormField id="phone" label="Phone Number" placeholder="+880 1XXX XXXXXX" value={reg.phone} onChange={handleRegChange} error={regErrors.phone} icon={<Phone size={13} />} />
                    <FormField id="roll" label="Student Roll" placeholder="120220503" value={reg.roll} onChange={handleRegChange} error={regErrors.roll} icon={<Hash size={13} />} />
                  </div>
                  <button className="btn btn-primary btn-lg" type="submit" style={{ width: '100%', justifyContent: 'center' }} disabled={regStatus === 'loading'}>
                    {regStatus === 'loading' ? <><Loader2 size={14} className="spin" /> Registering…</> : <><Rocket size={13} /> Confirm Registration</>}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventModal;
